import { Text, Box, Button, Flex } from "@chakra-ui/react";
import { useMobileBreakpoint } from "../../../../utils/useBreakpoints";
import { InfoIcon } from "../../../../components/icons/reader/InfoIcon";
import { FollowingIcon } from "../../../../components/icons/FollowingIcon";
import { FollowIcon } from "../../../../components/icons/FollowIcon";
import { BookmarkIcon } from "../../../../components/icons/BookmarkIcon";
import { ChatIcon } from "../../../../components/icons/ChatIcon";
import { motion } from "framer-motion";
import { cloneElement } from "react";
import { t } from "i18next";
import { useSlideInMenu } from "../../../../components/slideInMenu/hooks/useSlideInMenu";
import { BookmarkCheckedIcon } from "../../../../components/icons/reader/BookmarkCheckedIcon";
import { useComicData } from "../../hooks/hookstate/useComicData";
import { FUNNEL_TYPES } from "../../../../components/funnel/utils/contants";
import { FunnelContextArea } from "../../../../components/funnel/types";
import { Artist, Comic } from "../../../../types";
import { useQuery, skipToken } from "@tanstack/react-query";
import { useToggleArtistFollow } from "../../../../components/cards/hooks/useToggleArtistFollow";
import { SLIDE_IN_MENUS } from "../../../root/constants";
import { useReleaseData } from "../../hooks/hookstate/useReleaseData";
import { useUserData } from "../../../../hooks/useUserData";
import { setFunnelModalConfig } from "../../../../components/funnel/FunnelModal.tsx";
import { formatLargeNumber } from "../../../../utils/formatLargeNumber";
import { useHookstate } from "@hookstate/core";
import { readerNavigationContext } from "../../../../hookstate/readerNavigationContextStore";

export function OverlayActions() {
  const { comicData } = useComicData();
  const isMobile = useMobileBreakpoint();
  const { userData } = useUserData();

  const artistCacheKey = `${comicData?.artist.entity_type}-${comicData?.artist.id}`;
  const { handleToggleArtistFollow } = useToggleArtistFollow(artistCacheKey);
  const { releaseData } = useReleaseData();

  const { openComicInfoMenu, onToggleSlideInMenu } = useSlideInMenu();
  const readerContext = useHookstate(readerNavigationContext).get({
    noproxy: true,
  });

  const cacheKey = `${comicData?.entity_type}-${comicData?.id}`;
  const { data: cachedComicData } = useQuery<Comic>({
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const { data: artistData } = useQuery<Artist>({
    queryKey: [artistCacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const userIsFollowing = artistData?.user_is_following;
  return (
    <Box
      as={motion.div}
      animate={{ x: [0, -25], opacity: [0, 1] }}
      position="absolute"
      bottom={isMobile ? "100px" : "25px"}
      right={0}
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
      gap="8px"
    >
      {!isMobile && (
        <ReaderActionButton
          text={t("screens.reader.overlay.series_info")}
          icon={<InfoIcon />}
          buttonAction={() =>
            openComicInfoMenu(releaseData?.comic?.slug ?? "", {
              ...(readerContext ?? {}),
              area: "consumption",
            })
          }
        />
      )}
      <ReaderActionButton
        text={
          userIsFollowing
            ? t("screens.reader.overlay.unfollow")
            : t("screens.reader.overlay.follow")
        }
        color={userIsFollowing ? "blaze.400" : "white"}
        icon={userIsFollowing ? <FollowingIcon /> : <FollowIcon />}
        buttonAction={() => {
          handleToggleArtistFollow();
        }}
      />
      <ReaderActionButton
        text={
          comicData?.library_status
            ? t("screens.reader.overlay.manage")
            : t("screens.reader.overlay.add_to")
        }
        color={cachedComicData?.library_status ? "blaze.400" : "white"}
        icon={
          cachedComicData?.library_status ? (
            <BookmarkCheckedIcon />
          ) : (
            <BookmarkIcon />
          )
        }
        buttonAction={() => {
          const context = {
            modalContext: FunnelContextArea.addToLibrary,
            cacheKey,
          };
          if (userData) {
            setFunnelModalConfig({
              type: FUNNEL_TYPES.addToLibrary,
              context,
            });
          } else {
            setFunnelModalConfig({
              type: FUNNEL_TYPES.signup,
              context,
              options: {
                onSuccess: () => {
                  setFunnelModalConfig({
                    type: FUNNEL_TYPES.addToLibrary,
                    context,
                  });
                },
              },
            });
          }
        }}
      />
      <ReaderActionButton
        text={t("screens.reader.overlay.comments")}
        icon={<ChatIcon />}
        buttonAction={() => onToggleSlideInMenu(SLIDE_IN_MENUS.comments)}
        count={releaseData?.total_comments ?? 0}
      />
    </Box>
  );
}

interface ReaderActionButtonProps {
  text: string;
  icon: JSX.Element;
  buttonAction?: () => void;
  color?: string;
  count?: number;
}
function ReaderActionButton({
  text,
  icon,
  buttonAction,
  color = "white",
  count,
}: ReaderActionButtonProps) {
  const isMobile = useMobileBreakpoint();
  return (
    <Button
      position="relative"
      variant={isMobile ? "readerActionIconButton" : "readerActionButton"}
      aria-label={text}
      color={color}
      onClick={(e) => {
        e.stopPropagation();
        buttonAction?.();
      }}
    >
      {!isMobile && (
        <Text fontSize="12px" lineHeight="16px" fontWeight="500">
          {text}
        </Text>
      )}
      {cloneElement(icon, { width: "28px", height: "28px" })}
      {count !== undefined && (
        <Flex
          position="absolute"
          top="-8px"
          marginRight="-75%"
          background="warning.300"
          rounded="full"
          alignItems="center"
          justifyContent="center"
          aspectRatio="1"
          p="2px"
          boxSizing="content-box"
          height="16px"
        >
          <Text variant="monospaceTime" color="black">
            {formatLargeNumber(count)}
          </Text>
        </Flex>
      )}
    </Button>
  );
}
